import clsx from 'clsx';
import { useState, useEffect, useCallback } from 'react';

import { IconEye, IconSearch, IconAdjustments } from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import DateRangePicker from '@/components/DateRangePicker';
import ExportDropdown from '@/components/ExportDropdown';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import CustomTable from '@/components/Table/index';
import { LessonStageConfig } from '@/constants/constant';
import { EExportType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import {
  getSchoolsDropdown,
  getParentsDropdown,
  getLearningProgress,
  exportLearningProgress,
} from '@/service/reportService';
import { ILearningProgressItem } from '@/types';
import {
  pickBy,
  downloadFile,
  customPredicate,
  formatLastActive,
  formatSecondsToDuration,
} from '@/utils';

import ChildLearningDetailsModal from './components/ChildLearningDetailsModal';
import LearningFilterModal from './components/LearningFilterModal';

// ─── Types ───────────────────────────────────────────────────────────────────

type TLearningParams = {
  search?: string;
  offset?: number;
  limit?: number;
  order?: Record<string, string>;
  school_id?: string[];
  parent_id?: string[];
  start_date?: number;
  end_date?: number;
};

const PAGE_SIZE = 10;

const Learning = () => {
  const { formatText } = useFormatText();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ASC' | 'DESC' | '';
  }>({ key: '', direction: '' });

  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [filters, setFilters] = useState<{
    school_id: string[];
    parent_id: string[];
  }>({
    school_id: [],
    parent_id: [],
  });

  const [isOpenDetails, setIsOpenDetails] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  const [schoolOptions, setSchoolOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [parentOptions, setParentOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [data, setData] = useState<ILearningProgressItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const handleExport = async (type: 'csv' | 'pdf') => {
    setIsExportLoading(true);
    try {
      const query: Record<string, unknown> = {
        search: debouncedSearch || undefined,
        school_id:
          filters?.school_id?.length > 0 ? filters?.school_id : undefined,
        parent_id:
          filters?.parent_id?.length > 0 ? filters?.parent_id : undefined,
        start_date: startDate
          ? Math.floor(new Date(startDate).getTime() / 1000)
          : undefined,
        end_date: endDate
          ? Math.floor(new Date(endDate).getTime() / 1000)
          : undefined,
        type: type === 'csv' ? EExportType.CSV : EExportType.PDF,
      };
      const res = await exportLearningProgress(query);
      if (res) {
        const label = formatText('report-analytics.learning.title')
          .replace(/\s+/g, '_')
          .toLowerCase();
        downloadFile(res, `${label}_${new Date().getTime()}.${type}`);
      }
    } catch (error) {
      console.error('Error exporting learning progress:', error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const fetchLearningProgress = useCallback(async () => {
    setIsLoading(true);

    let params: TLearningParams = {
      search: debouncedSearch || undefined,
      offset: (currentPage - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      order:
        sortConfig?.key && sortConfig?.direction
          ? { [sortConfig.key]: sortConfig.direction }
          : undefined,
      school_id:
        filters?.school_id?.length > 0 ? filters?.school_id : undefined,
      parent_id:
        filters?.parent_id?.length > 0 ? filters?.parent_id : undefined,

      start_date: startDate
        ? Math.floor(new Date(startDate).getTime() / 1000)
        : undefined,
      end_date: endDate
        ? Math.floor(new Date(endDate).getTime() / 1000)
        : undefined,
    };

    params = pickBy(params, customPredicate) as TLearningParams;

    const response = await getLearningProgress(
      params as Record<string, unknown>
    );
    if (response?.status) {
      setData(response?.data || []);
      setTotal(response?.total || 0);
    }

    setIsLoading(false);
  }, [currentPage, debouncedSearch, sortConfig, filters, startDate, endDate]);

  useEffect(() => {
    fetchLearningProgress();
  }, [fetchLearningProgress]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      const [schools, parents] = await Promise.all([
        getSchoolsDropdown(),
        getParentsDropdown(),
      ]);
      if (schools?.status) {
        setSchoolOptions(
          (schools?.data ?? [])?.map((s: { id: string; name: string }) => ({
            value: s.id,
            label: s.name,
          }))
        );
      }
      if (parents?.status) {
        setParentOptions(
          (parents?.data ?? [])?.map((p: { id: string; name: string }) => ({
            value: p.id,
            label: p.name,
          }))
        );
      }
    };
    fetchDropdowns();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters, startDate, endDate]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key
          ? prev.direction === 'ASC'
            ? 'DESC'
            : prev.direction === 'DESC'
              ? ''
              : 'ASC'
          : 'ASC',
    }));
  };

  const columns = [
    {
      title: 'report-analytics.learning.table.child-name',
      key: 'child_name',
      width: '20%',
      sorter: true,
      handleSort: () => handleSort('child_name'),
      render: (row: ILearningProgressItem) => (
        <div className="flex items-center gap-2">
          <AvatarGroupList
            list={[
              {
                id: row?.id,
                name: row?.child_name,
                image: row?.profile_pic ?? '',
              },
            ]}
            size={32}
            avatarClassName="w-8 h-8 text-[10px]"
          />
          <span className="text-sm font-medium text-neutral-primary whitespace-nowrap">
            {row.child_name}
          </span>
        </div>
      ),
    },
    {
      title: 'report-analytics.learning.table.associated-parent',
      key: 'associated_parent',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('associated_parent'),
      render: (row: ILearningProgressItem) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row?.associated_parent || '-'}
        </span>
      ),
    },
    {
      title: 'report-analytics.learning.table.school',
      key: 'school',
      width: '12%',
      sorter: true,
      handleSort: () => handleSort('school'),
      render: (row: ILearningProgressItem) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row?.school || '-'}
        </span>
      ),
    },
    {
      title: 'report-analytics.learning.table.level',
      key: 'level',
      width: '10%',
      sorter: true,
      handleSort: () => handleSort('level'),
      render: (row: ILearningProgressItem) => {
        const config = LessonStageConfig[row.stage];
        return (
          <span
            className={clsx(
              'px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap',
              config?.className
            )}
          >
            {formatText(config?.label)}
          </span>
        );
      },
    },
    {
      title: 'report-analytics.learning.table.game-count',
      key: 'game_count',
      width: '8%',
      sorter: true,
      handleSort: () => handleSort('game_count'),
      render: (row: ILearningProgressItem) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row?.game_count}
        </span>
      ),
    },
    {
      title: 'report-analytics.learning.table.progress',
      key: 'progress',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('progress'),
      render: (row: ILearningProgressItem) => (
        <ProgressBar
          value={row.progress}
          showPercentage={true}
          barColor="bg-brand-default"
          height="h-2"
          className="w-full min-w-50"
        />
      ),
    },
    {
      title: 'report-analytics.learning.table.time',
      key: 'play_time',
      width: '8%',
      sorter: true,
      handleSort: () => handleSort('play_time'),
      render: (row: ILearningProgressItem) => (
        <span className="text-sm font-medium text-neutral-primary whitespace-nowrap">
          {row?.play_time ? formatSecondsToDuration(row.play_time) : '-'}
        </span>
      ),
    },
    {
      title: 'report-analytics.learning.table.last-update',
      key: 'last_update',
      width: '10%',
      sorter: true,
      handleSort: () => handleSort('last_update'),
      render: (row: ILearningProgressItem) => (
        <span className="text-sm font-medium text-neutral-secondary whitespace-nowrap">
          {formatLastActive(row?.last_update ?? undefined, formatText)}
        </span>
      ),
    },
    {
      title: 'report-analytics.learning.table.action',
      key: 'action',
      width: '4%',
      align: 'center' as const,
      render: (row: ILearningProgressItem) => (
        <ActionDropdown
          actions={[
            {
              key: 'view',
              label: 'common.actions.view',
              icon: <IconEye size={18} />,
              onClick: () => {
                setSelectedChildId(row.id);
                setIsOpenDetails(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  const isFilterApplied =
    filters?.school_id?.length > 0 || filters?.parent_id?.length > 0;

  const hasActiveFilters =
    debouncedSearch?.length > 0 || isFilterApplied || !!startDate || !!endDate;

  const isEmpty = data?.length === 0 && !isLoading && !hasActiveFilters;
  const isEmptySearch = data?.length === 0 && !isLoading && hasActiveFilters;

  return (
    <div className="flex flex-col gap-6">
      {/* Header Filters row */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <Input
            name="search"
            placeholder={formatText(
              'report-analytics.learning.search-placeholder'
            )}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="min-w-70"
            inputClassName="py-[12px]"
          />
          <div className="relative">
            <Button
              icon={<IconAdjustments size={20} />}
              label={formatText('common.actions.filter')}
              variant="outline"
              className={clsx(
                'px-4 h-12 border-neutral-light-200',
                isFilterApplied && 'border-brand-default! text-brand-default!'
              )}
              onClick={() => setIsOpenFilter(true)}
            />
            {isFilterApplied && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
            )}
          </div>
        </div>

        <div className="flex justify-center items-center gap-2.5">
          <DateRangePicker
            placeholder={formatText(
              'report-analytics.learning.filter-date-range-placeholder'
            )}
            setFirstDate={setStartDate}
            setSecondDate={setEndDate}
            popupAlign="right"
            className="w-full! min-w-0! h-12! text-base! font-normal!"
          />
          <ExportDropdown
            onCsvExport={() => handleExport('csv')}
            onPdfExport={() => handleExport('pdf')}
            disabled={isExportLoading}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-neutral-primary">
          {formatText('report-analytics.learning.title')}
        </h2>

        <CustomTable
          dataSource={data}
          columns={columns}
          isLoading={isLoading}
          isEmpty={isEmpty}
          isEmptySearch={isEmptySearch}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={total}
          fieldName={sortConfig.key}
          sortDirection={sortConfig.direction}
          rootClassName="lg:h-[calc(100vh-320px)]"
          onRowClick={(id) => {
            setSelectedChildId(id);
            setIsOpenDetails(true);
          }}
        />
      </div>

      {isOpenFilter && (
        <LearningFilterModal
          setIsOpen={setIsOpenFilter}
          onApply={setFilters}
          initialValues={filters}
          schoolOptions={schoolOptions}
          parentOptions={parentOptions}
        />
      )}

      {isOpenDetails && (
        <ChildLearningDetailsModal
          setIsOpen={setIsOpenDetails}
          childId={selectedChildId}
        />
      )}
    </div>
  );
};

export default Learning;
